import { useEffect, useMemo, useRef, useState } from 'react';
import { ARMADORES, fetchQuienArmo, limpiarCacheQuienArmo } from './quienArmoService';

const COLORES = {
  DM: { text: '#db2777', border: '#ec4899', bg: 'rgba(236, 72, 153, 0.08)', bar: 'linear-gradient(90deg, #ec4899, #f472b6)' },
  DD: { text: '#2563eb', border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.08)', bar: 'linear-gradient(90deg, #3b82f6, #60a5fa)' },
  OC: { text: '#d97706', border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)', bar: 'linear-gradient(90deg, #f59e0b, #fbbf24)' },
  JG: { text: '#0d9488', border: '#42C3BA', bg: 'rgba(66, 195, 186, 0.08)', bar: 'linear-gradient(90deg, #42C3BA, #2dd4bf)' },
  SIN: { text: '#64748b', border: '#94a3b8', bg: 'rgba(148, 163, 184, 0.08)', bar: 'linear-gradient(90deg, #94a3b8, #cbd5e1)' },
};

const ANIO_ACTUAL = new Date().getFullYear();
const PERIODOS = [
  { id: 'anio', label: String(ANIO_ACTUAL) },
  { id: 'anioAnterior', label: String(ANIO_ACTUAL - 1) },
  { id: 'todo', label: 'Todo' },
];

function filtrarPorPeriodo(stros, periodo) {
  if (periodo === 'todo') return stros;
  const anio = periodo === 'anio' ? ANIO_ACTUAL : ANIO_ACTUAL - 1;
  return stros.filter(s => (s.fechaDenuncia || s.fechaStro || '').startsWith(String(anio)));
}

export default function DashboardQuienArmo({ stros, scopeLabel }) {
  const [periodo, setPeriodo] = useState('anio');
  const [valores, setValores] = useState({}); // id -> codigo | null
  const [cargando, setCargando] = useState(false);
  const [progreso, setProgreso] = useState({ hechos: 0, total: 0 });
  const [activo, setActivo] = useState(null); // codigo seleccionado para la mini tabla
  const [reintento, setReintento] = useState(0);
  const senalRef = useRef(null);

  const strosPeriodo = useMemo(() => filtrarPorPeriodo(stros, periodo), [stros, periodo]);
  const ids = useMemo(() => strosPeriodo.map(s => s.id).filter(Boolean), [strosPeriodo]);
  const idsKey = ids.join(',');

  useEffect(() => {
    if (!ids.length) {
      if (senalRef.current) senalRef.current.cancelado = true;
      setValores({});
      setCargando(false);
      return;
    }
    // cancelar la carga anterior si cambió el set
    if (senalRef.current) senalRef.current.cancelado = true;
    const senal = { cancelado: false };
    senalRef.current = senal;

    setCargando(true);
    setProgreso({ hechos: 0, total: ids.length });
    fetchQuienArmo(ids, {
      signal: senal,
      onProgress: (hechos, total) => {
        if (!senal.cancelado) setProgreso({ hechos, total });
      },
    }).then(res => {
      if (senal.cancelado) return;
      setValores(res);
      setCargando(false);
    });

    return () => {
      senal.cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, reintento]);

  const conteo = useMemo(() => {
    const c = { DM: 0, DD: 0, OC: 0, JG: 0, SIN: 0 };
    ids.forEach(id => {
      const v = valores[id];
      if (v && c[v] !== undefined) c[v]++;
      else if (v === null) c.SIN++;
    });
    return c;
  }, [ids, valores]);

  const conDato = conteo.DM + conteo.DD + conteo.OC + conteo.JG;
  const maxConteo = Math.max(1, conteo.DM, conteo.DD, conteo.OC, conteo.JG);
  const respondidos = ids.filter(id => valores[id] !== undefined).length;

  const filas = [...ARMADORES.map(a => ({ ...a, count: conteo[a.codigo] })).sort((a, b) => b.count - a.count), { codigo: 'SIN', nombre: 'Sin dato', count: conteo.SIN }];

  const strosActivos = useMemo(() => {
    if (!activo) return [];
    return strosPeriodo
      .filter(s => (activo === 'SIN' ? valores[s.id] === null : valores[s.id] === activo))
      .sort((a, b) => (b.fechaDenuncia || '').localeCompare(a.fechaDenuncia || ''))
      .slice(0, 50);
  }, [activo, strosPeriodo, valores]);

  const refrescar = () => {
    limpiarCacheQuienArmo(ids);
    setReintento(n => n + 1);
  };

  return (
    <div className='dashboard-card quien-armo-card'>
      <div className='dashboard-card-header'>
        <span className='dashboard-card-title'>
          <i className='bi bi-folder-check'></i>
          Carpetas armadas por
        </span>
        <div className='quien-armo-header-right'>
          <div className='quien-armo-periodos'>
            {PERIODOS.map(p => (
              <button key={p.id} className={`quien-armo-periodo ${periodo === p.id ? 'quien-armo-periodo--active' : ''}`} onClick={() => setPeriodo(p.id)} disabled={cargando}>
                {p.label}
              </button>
            ))}
          </div>
          <span className='dashboard-card-badge' title={scopeLabel}>
            {conDato} de {ids.length}
          </span>
          <button className='quien-armo-refresh' onClick={refrescar} disabled={cargando || !ids.length} title='Volver a consultar (ignora la caché)'>
            <i className={`bi bi-arrow-clockwise ${cargando ? 'quien-armo-girando' : ''}`}></i>
          </button>
        </div>
      </div>

      {scopeLabel ? <div className='quien-armo-scope'>{scopeLabel}</div> : null}

      {cargando ? (
        <div className='quien-armo-progreso'>
          <div className='quien-armo-progreso-barra'>
            <div style={{ width: `${progreso.total ? Math.round((progreso.hechos * 100) / progreso.total) : 0}%` }} />
          </div>
          <span>
            Consultando planillas… {progreso.hechos}/{progreso.total}
          </span>
        </div>
      ) : null}

      {!ids.length ? (
        <div className='dashboard-empty'>
          <i className='bi bi-inbox'></i>
          <p>Sin siniestros en el período</p>
        </div>
      ) : (
        <div className='quien-armo-lista'>
          {filas.map(f => {
            const color = COLORES[f.codigo];
            const esSin = f.codigo === 'SIN';
            const pct = esSin ? (ids.length ? Math.round((f.count * 100) / ids.length) : 0) : conDato ? Math.round((f.count * 100) / conDato) : 0;
            const ancho = esSin ? (ids.length ? (f.count * 100) / ids.length : 0) : (f.count * 100) / maxConteo;
            const isActive = activo === f.codigo;
            return (
              <button
                key={f.codigo}
                className={`quien-armo-fila ${isActive ? 'quien-armo-fila--active' : ''} ${esSin ? 'quien-armo-fila--sin' : ''}`}
                style={{ '--fila-border': isActive ? color.border : '#e2e8f0', '--fila-bg': isActive ? color.bg : 'white' }}
                onClick={() => setActivo(prev => (prev === f.codigo ? null : f.codigo))}
                title={esSin ? 'Siniestros sin "quién lo armó" cargado en la planilla' : `${f.nombre} (${f.codigo})`}>
                <span className='quien-armo-avatar' style={{ background: color.bg, color: color.text, borderColor: color.border }}>
                  {esSin ? <i className='bi bi-dash'></i> : f.codigo}
                </span>
                <span className='quien-armo-nombre'>
                  {f.nombre}
                  {!esSin ? <small>{f.codigo}</small> : null}
                </span>
                <span className='quien-armo-barra'>
                  <span style={{ width: `${ancho}%`, background: color.bar }} />
                </span>
                <span className='quien-armo-count' style={{ color: color.text }}>
                  {f.count}
                </span>
                <span className='quien-armo-pct'>{pct}%</span>
              </button>
            );
          })}
        </div>
      )}

      {activo ? (
        <>
          <div className='estado-filtros-table-header'>
            <span className='estado-filtros-table-title'>
              <i className='bi bi-list-ul'></i>
              {activo === 'SIN' ? 'Sin dato' : `${ARMADORES.find(a => a.codigo === activo)?.nombre} (${activo})`}
            </span>
            <span className='estado-filtros-table-count'>
              {strosActivos.length < (activo === 'SIN' ? conteo.SIN : conteo[activo]) ? `${strosActivos.length} de ${activo === 'SIN' ? conteo.SIN : conteo[activo]}` : `${strosActivos.length} siniestros`}
            </span>
          </div>
          <div className='estado-filtros-table-wrapper'>
            <table className='estado-filtros-table'>
              <thead>
                <tr>
                  <th>Nro Stro</th>
                  <th>Patente</th>
                  <th>Asignado</th>
                  <th>Denuncia</th>
                </tr>
              </thead>
              <tbody>
                {strosActivos.map(s => (
                  <tr key={s.id} onClick={() => window.open(`/#/${s.id}`, '_blank')} style={{ cursor: 'pointer' }}>
                    <td className='estado-filtros-nrostro'>{s.nroStro || s.entryId || '—'}</td>
                    <td className='estado-filtros-asegurado'>{s.dom || s.nombreAseg || '—'}</td>
                    <td>
                      <span className='estado-filtros-badge'>{s.empleadoAsignado || '—'}</span>
                    </td>
                    <td>{s.fechaDenuncia ? new Date(s.fechaDenuncia + 'T00:00:00').toLocaleDateString('es-AR') : '—'}</td>
                  </tr>
                ))}
                {strosActivos.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                      Sin siniestros para mostrar
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {!cargando && respondidos < ids.length ? (
        <div className='quien-armo-aviso'>
          <i className='bi bi-exclamation-triangle'></i>
          {ids.length - respondidos} planilla{ids.length - respondidos > 1 ? 's' : ''} no respondieron. Usá el botón de actualizar para reintentar.
        </div>
      ) : null}
    </div>
  );
}
